import type { CopilotContextRef } from '@fos/shared/contracts/copilot';
import { Sparkles } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useSession } from '../../../lib/auth';
import { Button, type ButtonProps } from '../../../ui/Button';
import { askAi, useAskAiState } from '../ask-ai-store';
import { copilotChat } from '../chat-store';
import { useScreenContext } from '../screen-context';
import { AskAiPanel } from './AskAiPanel';

/**
 * Host panelu „Zapytaj AI”: montowany raz w szkielecie aplikacji (np. w `AppShell` obok `CommandPalette`), otwierany
 * przez `askAi.open({ contextRef, contextLabel })` z ⌘K, Dziś, widoków i bąbla AI (Fala 10). Rozmowa zostaje po
 * zamknięciu panelu i przejściu na inny ekran (sesja karty, `chat-store`; „Nowa rozmowa” czyści).
 *
 * Kontekst pytań: jawny z `askAi.open({ contextRef })` na ekranie, na którym panel otwarto; poza nim — kontekst
 * bieżącego ekranu z adresu (profil klienta → „ten klient”, karta zadania → „to zadanie”; `useScreenContext`).
 */
export function AskAiHost() {
  const state = useAskAiState();
  const session = useSession().data;
  const scope = session?.authenticated ? `${session.user.id}:${session.workspace?.id ?? '-'}` : null;
  useEffect(() => copilotChat.setScope(scope), [scope]);

  const { pathname } = useLocation();
  const screen = useScreenContext();
  const openedOn = useRef({ at: -1, path: '' });
  if (state.openedAt !== openedOn.current.at) openedOn.current = { at: state.openedAt, path: pathname };
  const explicit = state.contextRef !== null && openedOn.current.path === pathname;

  return (
    <AskAiPanel
      open={state.open}
      onOpenChange={(open) => {
        if (!open) askAi.close();
      }}
      contextRef={explicit ? state.contextRef : screen.ref}
      contextLabel={explicit ? state.contextLabel : screen.label}
      draft={state.draft}
      openedAt={state.openedAt}
      voice={state.voice}
    />
  );
}

/**
 * Przycisk „Zapytaj AI” (Dziś, nagłówki widoków): otwiera panel z kontekstem bieżącego widoku. Wymaga `<AskAiHost />`.
 */
export function AskAiButton({
  contextRef,
  contextLabel,
  label = 'Zapytaj AI',
  variant = 'secondary',
  size,
  className,
}: {
  contextRef?: CopilotContextRef | null;
  contextLabel?: string | null;
  label?: string;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  className?: string;
}) {
  return (
    <Button
      variant={variant}
      size={size}
      icon={Sparkles}
      className={className}
      onClick={() => askAi.open({ contextRef: contextRef ?? null, contextLabel: contextLabel ?? null })}
    >
      {label}
    </Button>
  );
}
